import { FC } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ApproverNote, { hasApproverNote } from '../ApproverNote';
import ArtifactTable from '../ArtifactTable';
import DownloadReportButton from '../DownloadReportButton';
import { SendTabProps } from '../ReportDialog.props';
import { reportDialogColors as colors } from '../ReportDialog.styles';
import { formatEmailDelivery, formatSftpDelivery, formatWhen } from '../ReportDialog.utils';

const SendTab: FC<SendTabProps> = ({
  columns,
  rows,
  day,
  recipients,
  isSent,
  isFinalized,
  isSending,
  onSend,
  canDownloadReport,
  isDownloadingDocuments,
  onDownload,
  onDownloadAttachment,
}) => {
  // Delivery and the approver record used to stack, which put the Send button a
  // scroll below the report. Side by side they both fit. Only two columns when
  // there is an approval to read -- otherwise delivery keeps the full width.
  const showApproval = hasApproverNote(day);

  return (
    <Box data-testid="daily-nav-report-send">
      <ArtifactTable columns={columns} rows={rows} />
      <Box
        mt={2}
        display="flex"
        gap={3}
        alignItems="flex-start"
        flexDirection={{ xs: 'column', md: 'row' }}>
        <Box flex={1} minWidth={0} data-testid="daily-nav-send-delivery">
          <Typography variant="body2">
            Recipients: {recipients.length ? recipients.join(', ') : 'None configured'}
          </Typography>
          <Typography variant="body2" mt={1} data-testid="daily-nav-email-delivery">
            {formatEmailDelivery(day, recipients, isSent)}
          </Typography>
          <Typography variant="body2" mt={1} data-testid="daily-nav-sftp-delivery">
            {formatSftpDelivery(day, isSent)}
          </Typography>
          {(day?.send_warnings ?? []).map(warning => (
            <Typography key={warning} variant="body2" mt={1} color="error.main" data-testid="daily-nav-send-warning">
              {warning}
            </Typography>
          ))}
          {isSent ? (
            <>
              <Typography variant="body2" mt={1} data-testid="daily-nav-sent-by">
                Sent by {day?.sent_by_name || 'Unknown'}
              </Typography>
              <Typography variant="body2" mt={1} data-testid="daily-nav-sent-at">
                Sent at {formatWhen(day?.sent_at)}
              </Typography>
            </>
          ) : (
            <Button
              data-testid="daily-nav-send-button"
              variant="contained"
              sx={{ mt: 1, textTransform: 'none' }}
              disabled={!isFinalized || isSending}
              onClick={onSend}>
              Send report
            </Button>
          )}
          {canDownloadReport && <DownloadReportButton isDownloading={isDownloadingDocuments} onClick={onDownload} />}
        </Box>

        {showApproval && (
          <Box
            flex={1}
            minWidth={0}
            data-testid="daily-nav-send-approval"
            sx={{
              borderLeft: { md: `1px solid ${colors.border}` },
              pl: { md: 3 },
            }}>
            <ApproverNote day={day} onDownloadAttachment={onDownloadAttachment} topMargin={0} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SendTab;
